import { doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import store from '@store/index';
import { CashFlow, cashFlowsRef } from '@api/CashFlows/CashFlow';
import {
    RecurringExpense,
    recurringExpensesRef,
    RECURRING_STATUS_LABELS,
    RecurringStatus
} from '@api/RecurringExpenses/RecurringExpense';
import UserServ from '@api/Users/UserServ';
import CashRegisterServ from '@api/CashRegister/CashRegisterServ';
import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { cashText } from '@api/ActivityLogs/CashActivity';
import { describeConfirmedExpense, EXPENSE_ACTIVITY_SECTION } from '@api/ActivityLogs/ExpenseActivity';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { isActiveCashRegister } from '@utils/CashRegisters';
import { buildInstanceConfirmation, periodOf, validateConfirmInstance, validateRecurringManage } from '@utils/Expenses';

export const RECURRING_ACTIVITY_SECTION = 'recurringExpense';

const currentAuthor = async (): Promise<{ uid: string; name: string }> => {
    const user = await UserServ.getUser();
    return { uid: user.uid, name: user.profile?.name || user.email || '' };
};

const inBranches = (branchOfficeId: string | undefined, branches: 'all' | string[]): boolean =>
    branches === 'all' || (!!branchOfficeId && branches.includes(branchOfficeId));

export default {
    /**
     * Las plantillas de las sucursales visibles, sin las eliminadas. Lecturas: la
     * colección entera, que son decenas (un par de fijos por sucursal); el filtro de
     * sucursal va en el cliente para no pedir índice.
     */
    async getRecurringExpenses(branches: 'all' | string[]): Promise<RecurringExpense[]> {
        const snapshot = await getDocs(recurringExpensesRef);
        return snapshot.docs
            .map((document) => ({ ...(document.data() as RecurringExpense), id: document.id }))
            .filter((template) => template.status !== 'deleted' && inBranches(template.branchOfficeId, branches));
    },

    /** La escribe `ExpenseServ.saveExpense` al registrar el primer gasto del fijo. */
    async createRecurringExpense(template: RecurringExpense): Promise<void> {
        await setDoc(doc(recurringExpensesRef, template.id), template);
    },

    /** Pausar, reanudar o eliminar. No toca los meses ya generados. */
    async setRecurringStatus(template: RecurringExpense, status: RecurringStatus): Promise<void> {
        const problem = validateRecurringManage(template, getCurrentPermissionSubject());
        if (problem) throw new Error(problem);
        // Reanudar no rellena los meses pausados: el aseo genera desde el mes siguiente a
        // `lastPeriod`, así que se adelanta al mes anterior (nunca se atrasa).
        const resumeFrom = periodOf(new Date(), -1);
        const lastPeriod =
            status === 'active' && template.status === 'paused' && template.lastPeriod < resumeFrom ? resumeFrom : undefined;
        await updateDoc(doc(recurringExpensesRef, template.id), {
            status,
            updatedAt: new Date(),
            ...(lastPeriod ? { lastPeriod } : {})
        });
        const verbo = status === 'active' ? 'reanudó' : status === 'paused' ? 'pausó' : 'eliminó';
        await ActivityLogServ.addActivityLog(
            RECURRING_ACTIVITY_SECTION,
            template.id,
            `${verbo} el gasto fijo "${template.description}" (${RECURRING_STATUS_LABELS[status].toLowerCase()})`
        );
    },

    /** El monto con el que nacen los meses siguientes (los ya generados no cambian). */
    async setRecurringAmount(template: RecurringExpense, amount: number): Promise<void> {
        const problem = validateRecurringManage(template, getCurrentPermissionSubject());
        if (problem) throw new Error(problem);
        if (!(amount > 0)) throw new Error('El monto tiene que ser mayor a cero');
        const rounded = Math.round(amount * 100) / 100;
        await updateDoc(doc(recurringExpensesRef, template.id), { amount: rounded, updatedAt: new Date() });
        await ActivityLogServ.addActivityLog(
            RECURRING_ACTIVITY_SECTION,
            template.id,
            `cambió el monto del gasto fijo de ${cashText(template.amount)} a ${cashText(rounded)} (desde el próximo mes)`
        );
    },

    /**
     * Los estimados que el aseo generó y nadie ha confirmado, de las sucursales
     * visibles. Consulta de UN campo (`pendingConfirmation == true`): son pocos (un
     * puñado por sucursal al mes), la sucursal se filtra en el cliente.
     */
    async getPendingInstances(branches: 'all' | string[]): Promise<CashFlow[]> {
        const snapshot = await getDocs(query(cashFlowsRef, where('pendingConfirmation', '==', true)));
        return snapshot.docs
            .map((document) => ({ ...(document.data() as CashFlow), id: document.id }))
            .filter((flow) => flow.status !== 'deleted' && inBranches(flow.branchOfficeId, branches));
    },

    /**
     * Confirma un estimado con el monto del recibo (§8.5). Si sale de la caja, tiene
     * que haber caja abierta en ESA sucursal (la de la sesión). Actualiza el monto de
     * la plantilla: con él nace el mes siguiente. 2 escrituras + bitácora.
     */
    async confirmInstance(flow: CashFlow, amount: number, paidFromCash: boolean): Promise<void> {
        if (!flow.id) throw new Error('El gasto no tiene id');
        const { auth, krispis } = store.getState();
        // Sacarlo de la caja exige la caja abierta DE VERDAD (confirmada contra Firestore,
        // 1 lectura): la de la sesión puede estar vieja y el gasto caería fuera de todo corte.
        const hasOpenCashRegister = paidFromCash
            ? await CashRegisterServ.confirmActiveCashRegister(flow.branchOfficeId)
            : flow.branchOfficeId === auth.branchOfficeSelected?.id &&
              isActiveCashRegister(krispis.cashRegister, flow.branchOfficeId);
        const problem = validateConfirmInstance(flow, amount, paidFromCash, {
            subject: getCurrentPermissionSubject(),
            hasOpenCashRegister
        });
        if (problem) throw new Error(problem);
        const author = await currentAuthor();
        const confirmation = buildInstanceConfirmation(amount, paidFromCash, author, new Date());
        await updateDoc(doc(cashFlowsRef, flow.id), confirmation);
        if (flow.recurringExpenseId) {
            try {
                await updateDoc(doc(recurringExpensesRef, flow.recurringExpenseId), {
                    amount: confirmation.amount,
                    updatedAt: new Date()
                });
            } catch {
                // La plantilla pudo haberse eliminado: el gasto ya quedó confirmado.
            }
        }
        await ActivityLogServ.addActivityLog(
            EXPENSE_ACTIVITY_SECTION,
            flow.id,
            describeConfirmedExpense(flow, confirmation.amount || 0, paidFromCash)
        );
    }
};
